/**
 * Section Storage Management for Last Used Section Tracking
 * Manages localStorage for redirecting non-logged-in users to their last visited section
 */


class SectionStorage {
  static SECTION_KEY = 'af_last_section';
  static REDIRECT_PREVENTION_KEY = 'af_redirect_prevention';
  static REDIRECT_PREVENTION_DURATION = 15 * 60 * 1000;
  static SEARCH_HASH_REGEX = /^#\/?search/;

  constructor() {
    this.sectionMap = [...document.querySelectorAll(
      'main-navigation > .\\#main-navigation-list > .\\#main-navigation-item > .\\#main-navigation-link'
    )]
      .map(n => n.textContent.toLowerCase().trim())
      .filter(Boolean)
      .reduce((acc, item) => {
        const items = [
          item,
          item.replaceAll(' & ', '-').replaceAll(' ', '-'),
          ...item.split(' & ').map(i => i.trim())
        ];
        items.forEach(i => acc.set(i, item));
        return acc
      }, new Map())

  }

  static setStorage(name, value, lifespan = null) {
    const timestamp = Date.now()
    const expires = lifespan ? timestamp + lifespan : null;

    const data = {
      value,
      timestamp,
    };

    if (expires) {
      data['expires'] = expires;
    }

    localStorage.setItem(name, JSON.stringify(data));
  }

  static getStorage(name) {
    try {
      const item = localStorage.getItem(name);
      if (!item) return;

      const data = JSON.parse(item);

      // Check if expired
      if (data.expires && Date.now() > data.expires) {
        localStorage.removeItem(name);
        return;
      }

      return data.value;
    } catch (e) {
      localStorage.removeItem(name); // Probably invalid data, clear it
    }
  }

  hasSection(section) {
    return this.sectionMap.has(section)
  }

  findSectionStartsWith(str) {
    return this.sectionMap.get(
      [...this.sectionMap.keys()]
        .find(key =>
          key.toLowerCase() === str ||
          str.toLowerCase().startsWith(`${key.toLowerCase()}-`)
        )
    )
  }

  setLastSection(section) {
    if (!this.hasSection(section)) return
    SectionStorage.setStorage(SectionStorage.SECTION_KEY, section);
  }

  getLastSection() {
    const lastSection = SectionStorage.getStorage(SectionStorage.SECTION_KEY);

    if (!this.hasSection(lastSection)) return
    return lastSection;
  }

  static clearRedirectPrevention() {
    localStorage.removeItem(SectionStorage.REDIRECT_PREVENTION_KEY);
  }

  static setRedirectPrevention() {
    SectionStorage.setStorage(
      SectionStorage.REDIRECT_PREVENTION_KEY,
      'true',
      SectionStorage.REDIRECT_PREVENTION_DURATION
    );
  }

  static isRedirectPrevented() {
    return SectionStorage.getStorage(SectionStorage.REDIRECT_PREVENTION_KEY) === 'true';
  }

  static isUserLoggedIn() {
    return !!window.customer
  }

  detectSectionFromUrl() {
    const path = window.location.pathname.toLowerCase().trim();

    if (!path.startsWith('/collections/')) return;

    const section = path.replace('/collections/', '').split('/')[0];
    return this.findSectionStartsWith(section);
  }

  detectSectionFromProduct() {
    if (!window.pageInfo.product) return

    const product = window.pageInfo.product;

    if (
      !product ||
      !product.collections ||
      !Array.isArray(product.collections)
    ) return;

    const foundSections = [...product.collections.reduce((acc, collection) => {
      const foundSection = this.findSectionStartsWith(collection.handle);
      if (foundSection) acc.add(foundSection);
      return acc;
    }, new Set())]

    // Ignore if we match too little or too many
    if (foundSections.length === 0 || foundSections.length > 1) return;
    return foundSections[0];
  }


  handlePageVisit() {
    // Don't track for logged-in users
    if (SectionStorage.isUserLoggedIn() || !window.pageInfo) return;

    const detections = [
      this.detectSectionFromUrl,
      this.detectSectionFromProduct
    ];

    for (const detect of detections) {
      const detectedSection = detect.call(this);
      if (detectedSection) return this.setLastSection(detectedSection);
    }
  }

  /**
   * Handle homepage visit and redirect if appropriate
   */
  static handleHomepageVisit() {
    if (SectionStorage.SEARCH_HASH_REGEX.test(window.location.hash)) return;

    if (SectionStorage.isRedirectPrevented()) {
      SectionStorage.setRedirectPrevention();
      return
    }

    SectionStorage.setRedirectPrevention();

    if (SectionStorage.isUserLoggedIn()
      && window.customer.businessType) {
        const businessTypePageMap = {
          'Hairdressing Salon': '/collections/hair',
          'Freelance Hairdresser': '/collections/hair',
          'Hair & Beauty Salon': '/collections/hair',
          'Freelance Hair & Beauty': '/collections/hair',
          'College': '/collections/hair',
          'Student': '/collections/hair',
          'Beauty Salon / Spa': '/collections/beauty',
          'Freelance Beautician': '/collections/beauty',
          'Barber': '/collections/barbers'
        };

        window.location.href = businessTypePageMap[window.customer.businessType] || '/';
    }

    const section = SectionStorage.getStorage(SectionStorage.SECTION_KEY);
    if (!section) return

    const newPage = section
      .replaceAll(' & ', '-')
      .replaceAll(' ', '-')
      .toLowerCase()
      // Weird explicit case for now
      .replace('beauty-nails', 'beauty')

    window.location.href = `/collections/${newPage}`;
  }

  /**
   * Reset the redirect prevention timer to extend the 15-minute window
   * Called when user browses to any page during the prevention period
   */
  static resetRedirectPreventionTimer() {
    // Only reset if user is not logged in and prevention is currently active
    if (
      SectionStorage.isUserLoggedIn() ||
      !SectionStorage.isRedirectPrevented()
    ) return
    SectionStorage.setRedirectPrevention();
  }
}


if (
  window.location.pathname === '/' ||
  window.location.pathname === ''
) SectionStorage.handleHomepageVisit()

if (window.location.pathname === '/account/login') {
  SectionStorage.clearRedirectPrevention();
}

document.addEventListener('DOMContentLoaded', () => {
  window.sectionStorage = new SectionStorage();
  // Handle page visit tracking for all other pages
  window.sectionStorage.handlePageVisit();

  // On non-homepage visits, reset the redirect prevention timer if active
  SectionStorage.resetRedirectPreventionTimer();
});
